/**
 * Frontend env reader. Vite exposes env vars on `import.meta.env` — only the
 * `VITE_*` prefix is bundled, keeping anything secret server-side.
 *
 * Defaults assume the local-dev port plan from BS-005:
 *   - service-mail               on http://localhost:3001
 *   - service-customer           on http://localhost:3040
 *   - service-pricing            on http://localhost:3030
 *   - service-audit              on http://localhost:3010
 *   - service-notification       on http://localhost:3020
 *   - service-payment            on http://localhost:3050
 *   - service-finance            on http://localhost:3060
 *   - service-delivery           on http://localhost:3080
 *   - service-kyc                on http://localhost:3090
 *   - service-government         on http://localhost:3100
 *   - service-financial-services on http://localhost:3110
 *   - service-service-desk       on http://localhost:3140
 *   - service-printing           on http://localhost:3150
 *   - service-franchise          on http://localhost:3160
 */
const SERVICE_MAIL_DEFAULT = 'http://localhost:3001';
const SERVICE_CUSTOMER_DEFAULT = 'http://localhost:3040';
const SERVICE_PRICING_DEFAULT = 'http://localhost:3030';
const SERVICE_NOTIFICATION_DEFAULT = 'http://localhost:3020';
const SERVICE_AUDIT_DEFAULT = 'http://localhost:3010';
const SERVICE_PAYMENT_DEFAULT = 'http://localhost:3050';
const SERVICE_FINANCE_DEFAULT = 'http://localhost:3060';
const SERVICE_DELIVERY_DEFAULT = 'http://localhost:3080';
const SERVICE_KYC_DEFAULT = 'http://localhost:3090';
const SERVICE_GOVERNMENT_DEFAULT = 'http://localhost:3100';
const SERVICE_FINANCIAL_SERVICES_DEFAULT = 'http://localhost:3110';
const SERVICE_SERVICE_DESK_DEFAULT = 'http://localhost:3140';
const SERVICE_PRINTING_DEFAULT = 'http://localhost:3150';
const SERVICE_FRANCHISE_DEFAULT = 'http://localhost:3160';
const SERVICE_GEO_DEFAULT = 'http://localhost:3170';
const SERVICE_REPORTING_API_DEFAULT = 'http://localhost:3120';
const SERVICE_REPORTING_ML_DEFAULT = 'http://localhost:3130';
const IDENTITY_DEFAULT = 'http://localhost:3013';

interface Env {
  serviceMailBaseUrl: string;
  serviceCustomerBaseUrl: string;
  servicePricingBaseUrl: string;
  serviceNotificationBaseUrl: string;
  serviceAuditBaseUrl: string;
  servicePaymentBaseUrl: string;
  serviceFinanceBaseUrl: string;
  serviceDeliveryBaseUrl: string;
  serviceKycBaseUrl: string;
  serviceGovernmentBaseUrl: string;
  serviceFinancialServicesBaseUrl: string;
  serviceServiceDeskBaseUrl: string;
  servicePrintingBaseUrl: string;
  serviceFranchiseBaseUrl: string;
  /** Base URL for `service-geo` — countries, states, NG LGAs, NG wards. Powers AddressFields. */
  serviceGeoBaseUrl: string;
  serviceReportingApiBaseUrl: string;
  serviceReportingMlBaseUrl: string;
  /** Base URL for `service-identity` — login form + 401 refresh interceptor target. */
  identityBaseUrl: string;
}

/**
 * Google Maps JavaScript API key. Returned verbatim from `VITE_GOOGLE_MAPS_API_KEY`,
 * or an empty string when the var is unset. UI surfaces (UpmpMap and the pages
 * that mount it) check for `''` and render a "Map disabled" notice instead of
 * loading the SDK — that keeps demos usable without a key and avoids Google's
 * console-loud "InvalidKeyMapError" when one isn't configured.
 *
 * Restrict the key by HTTP referrer in the Google Cloud Console — never ship
 * a wildcard key.
 */
export function googleMapsApiKey(): string {
  return import.meta.env['VITE_GOOGLE_MAPS_API_KEY'] ?? '';
}

export const env: Env = {
  serviceMailBaseUrl: import.meta.env['VITE_SERVICE_MAIL_BASE_URL'] ?? SERVICE_MAIL_DEFAULT,
  serviceCustomerBaseUrl:
    import.meta.env['VITE_SERVICE_CUSTOMER_BASE_URL'] ?? SERVICE_CUSTOMER_DEFAULT,
  servicePricingBaseUrl:
    import.meta.env['VITE_SERVICE_PRICING_BASE_URL'] ?? SERVICE_PRICING_DEFAULT,
  serviceNotificationBaseUrl:
    import.meta.env['VITE_SERVICE_NOTIFICATION_BASE_URL'] ?? SERVICE_NOTIFICATION_DEFAULT,
  serviceAuditBaseUrl:
    import.meta.env['VITE_SERVICE_AUDIT_BASE_URL'] ?? SERVICE_AUDIT_DEFAULT,
  servicePaymentBaseUrl:
    import.meta.env['VITE_SERVICE_PAYMENT_BASE_URL'] ?? SERVICE_PAYMENT_DEFAULT,
  serviceFinanceBaseUrl:
    import.meta.env['VITE_SERVICE_FINANCE_BASE_URL'] ?? SERVICE_FINANCE_DEFAULT,
  serviceDeliveryBaseUrl:
    import.meta.env['VITE_SERVICE_DELIVERY_BASE_URL'] ?? SERVICE_DELIVERY_DEFAULT,
  serviceKycBaseUrl: import.meta.env['VITE_SERVICE_KYC_BASE_URL'] ?? SERVICE_KYC_DEFAULT,
  serviceGovernmentBaseUrl:
    import.meta.env['VITE_SERVICE_GOVERNMENT_BASE_URL'] ?? SERVICE_GOVERNMENT_DEFAULT,
  serviceFinancialServicesBaseUrl:
    import.meta.env['VITE_SERVICE_FINANCIAL_SERVICES_BASE_URL'] ?? SERVICE_FINANCIAL_SERVICES_DEFAULT,
  serviceServiceDeskBaseUrl:
    import.meta.env['VITE_SERVICE_SERVICE_DESK_BASE_URL'] ?? SERVICE_SERVICE_DESK_DEFAULT,
  servicePrintingBaseUrl:
    import.meta.env['VITE_SERVICE_PRINTING_BASE_URL'] ?? SERVICE_PRINTING_DEFAULT,
  serviceFranchiseBaseUrl:
    import.meta.env['VITE_SERVICE_FRANCHISE_BASE_URL'] ?? SERVICE_FRANCHISE_DEFAULT,
  serviceGeoBaseUrl:
    import.meta.env['VITE_SERVICE_GEO_BASE_URL'] ?? SERVICE_GEO_DEFAULT,
  serviceReportingApiBaseUrl:
    import.meta.env['VITE_SERVICE_REPORTING_API_BASE_URL'] ?? SERVICE_REPORTING_API_DEFAULT,
  serviceReportingMlBaseUrl:
    import.meta.env['VITE_SERVICE_REPORTING_ML_BASE_URL'] ?? SERVICE_REPORTING_ML_DEFAULT,
  identityBaseUrl: import.meta.env['VITE_IDENTITY_BASE_URL'] ?? IDENTITY_DEFAULT,
};
